import { t } from "../../../../utils/i18n";
import React, { Component } from "react";
import { Formik, Form } from "formik";
import toastr from "toastr";
import CardTypes from "./components/CardTypes";
import Header from "./components/Header";
import PersonalDetails from "./components/PersonalDetails";
import IdentificationDetails from "./components/IdentificationDetails";
import MailingDetails from "./components/MailingDetails";
import Preview from "./components/Preview";
import { postRequest } from "../../../../utils/httpRequest";
import { formErrors } from "../../../../utils/formErrors";
import {
  cardRequestParams,
  formattedApiErrors,
  stepFromError,
} from "./helpers";
import { CardType } from "./Types";
import "./index.scss";

interface Props {
  params: {
    availableBalance: number;
    physicalCardTypes: CardType[];
    virtualCardTypes: CardType[];
    couponCode: string;
    canApplyForVirtualCard: boolean;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      isQAUser: boolean;
      dateOfBirth: string;
    };
  };

  isMultiRequest: boolean;
}

interface State {
  step: "cardType" | "personal" | "identification" | "mailing" | "preview";
  cardType: string;
  couponCode: string;
  countryCode: string;
  submitting: boolean;
  isCouponClaimable: boolean;
  isLoading: boolean;
  apiErrors?: any;
}

class New extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      step: props.isVirtual ? "personal" : "cardType",
      cardType: "plastic",
      couponCode: "",
      submitting: false,
      isCouponClaimable: false,
      isLoading: false,
      countryCode: "",
      apiErrors: {},
    };
  }

  handlePreviousStepChange = (step) => {
    this.setState({ step });
  };

  handleSetStep = (step) => {
    this.setState({ step });
  };
  handleNextStepChange = (validateForm, step) => {
    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        window.scrollTo(0, 0);
        this.setState({ step });
      }
    });
  };

  updateCouponCode = (couponCode) => {
    this.setState({ couponCode });
  };

  updateCountryCode = (countryCode) => {
    this.setState({ countryCode });
  };

  onSelectCardType = (e, cardType) => {
    const { couponCode } = this.state;

    e.preventDefault();

    this.setState({ cardType });

    if (cardType === "virtual") {
      this.setState({ isLoading: true });
      window.location.href = `/cards/requests/virtual?coupon_code=${couponCode}`;
    } else {
      this.handleSetStep("personal");
    }
  };

  confirmSubmission = (values) => {
    const { cardType } = this.state;
    if (
      !values.passport_bio_page ||
      !values.passport_with_selfie ||
      !values.digital_signature
    ) {
      this.handleSetStep("identification");
      toastr.error(t("cards.request_form.upload_all_documents"));
    } else {
      this.submit(values);
    }
  };

  submit = (values) => {
    const { cardType, isCouponClaimable, couponCode, countryCode } = this.state;
    this.setState({ submitting: true });

    const params = cardRequestParams(cardType, countryCode, values);

    const formData = new FormData();
    if (couponCode) {
      formData.append("coupon_code", couponCode);
    }

    Object.entries(params).forEach(([key, value]) => {
      formData.append(`card_account[${key}]`, value);
    });

    const url =
      cardType == "virtual"
        ? "/api/v1/cards/users/create_rain_account.json"
        : "/api/v1/cards/users.json";

    postRequest(url, formData, {
      "Content-Type": "multipart/form-data",
    })
      .then((response) => {
        window.location.href = `/cards/requests/${response.card_request_id}`;
      })
      .catch((error) => {
        console.log(error);
        this.setState({ submitting: false });

        if (error.data?.errors) {
          const errors = formErrors(error.data.errors);

          this.setState({
            apiErrors: formattedApiErrors(errors),
            step: stepFromError(error.data.errors),
          });
        }

        toastr.error(
          error?.error || error?.data?.error || t("cards.common.something_went_wrong")
        );
      });
  };

  render() {
    const {
      params: {
        user,
        availableBalance,
        physicalCardTypes,
        virtualCardTypes,
        canApplyForVirtualCard,
      },
      isMultiRequest,
    } = this.props;

    const { isLoading, step, cardType, submitting, apiErrors } = this.state;

    return (
      <div className="w-100">
        {isLoading ? (
          <div className="spinner-border" role="status">
            <span className="sr-only">{t("cards.request_form.loading")}</span>
          </div>
        ) : (
          <>
            <div className="create-offer-title">
              <h2 className="create-trade-page-title ml-3 mt-3">
                <i className="fa fa-credit-card"></i> {t("cards.request_form.apply_for_a_card")}
              </h2>
            </div>
            <Formik
              initialValues={{
                first_name: user.firstName,
                last_name: user.lastName,
                middle_name: "",
                name_on_card: `${user.firstName} ${user.lastName}`,
                salutation: "",
                occupation: "",
                gender: "male",
                phone_code: "",
                phone_number: "",
                annual_salary: "",
                account_purpose: "",
                expected_monthly_volume: "",
                passport_number: "",
                date_of_birth: "",
                place_of_birth: "",
                passport_issued_date: "",
                passport_expiry_date: "",
                address: "",
                city: "",
                state: "",
                country: "",
                postal_code: "",
                passport_bio_page: "",
                passport_with_selfie: "",
                digital_signature: "",
              }}
              onSubmit={(values) => this.confirmSubmission(values)}
            >
              {({
                setFieldValue,
                errors,
                values,
                handleChange,
                validateForm,
              }) => (
                <Form className="form">
                  {step === "cardType" && (
                    <CardTypes
                      availableBalance={availableBalance}
                      physicalCardTypes={physicalCardTypes}
                      virtualCardTypes={virtualCardTypes}
                      canApplyForVirtualCard={canApplyForVirtualCard}
                      isQAUser={user.isQAUser}
                      onSelectCardType={this.onSelectCardType}
                      updateCouponCode={this.updateCouponCode}
                    />
                  )}

                  <div className="steps card shadow-sm rounded-lg">
                    <div>
                      {step !== "cardType" && (
                        <div
                          className="col-md-12 mb-10"
                          style={{ marginBottom: "20px" }}
                        >
                          <Header
                            step={step}
                            handleSetStep={this.handleSetStep}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      {step === "personal" && (
                        <PersonalDetails
                          errors={errors}
                          values={values}
                          apiErrors={apiErrors}
                          setFieldValue={setFieldValue}
                          validateForm={validateForm as any}
                          handleNextStepChange={this.handleNextStepChange}
                          handlePreviousStepChange={
                            this.handlePreviousStepChange
                          }
                        />
                      )}
                      {step === "identification" && (
                        <IdentificationDetails
                          errors={errors}
                          apiErrors={apiErrors}
                          setFieldValue={setFieldValue}
                          validateForm={validateForm as any}
                          handleNextStepChange={this.handleNextStepChange}
                          handlePreviousStepChange={
                            this.handlePreviousStepChange
                          }
                        />
                      )}
                      {step === "mailing" && (
                        <MailingDetails
                          errors={errors}
                          apiErrors={apiErrors}
                          setFieldValue={setFieldValue}
                          selectedCountry={values.country}
                          isVirtualCardRequest={false}
                          validateForm={validateForm as any}
                          handleNextStepChange={this.handleNextStepChange}
                          handlePreviousStepChange={
                            this.handlePreviousStepChange
                          }
                          updateCountryCode={this.updateCountryCode}
                          isMultiRequest={isMultiRequest}
                        />
                      )}
                      {step === "preview" && (
                        <Preview
                          data={values}
                          handleSetStep={this.handleSetStep}
                          cardType={cardType}
                          physicalCardTypes={physicalCardTypes}
                          virtualCardTypes={virtualCardTypes}
                          submitting={submitting}
                          isMultiRequest={isMultiRequest}
                        />
                      )}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </>
        )}
      </div>
    );
  }
}

export default New;
